// =============================================================================
//  PROMOCIONES ESPECIALES POR CATEGORÍA
// =============================================================================
//  Aquí defines las categorías con promoción especial. Estas promociones:
//    - Tienen su propio precio de "paquete" (ej: 2 gorras x 50.000).
//    - El paquete SE REPITE: 4 unidades = 2 paquetes (100.000); una 5.ª unidad
//      suelta se cobra a su precio unitario (precioU).
//    - Los productos de estas categorías NO cuentan para el descuento global
//      por cantidad/total, y tampoco reciben ese descuento porcentual.
//
//  COMBINACIÓN ENTRE CATEGORÍAS (grupo):
//    Las categorías que pertenecen al MISMO grupo suman sus unidades juntas
//    para armar el paquete (1 monastery + 1 llamas = 2 unidades = 1 paquete
//    de 50.000). Por defecto se agrupan las categorías que tengan el MISMO
//    packSize y packPrice. Si necesitas que dos promociones con el mismo
//    precio NO se combinen, ponles un `grupo` distinto (texto libre).
//
//  Para agregar/editar una promoción, solo modifica el objeto de abajo.
//    clave      -> nombre de la categoría (nomCategoria). No importan
//                  mayúsculas ni acentos.
//    packSize   -> cuántas unidades forman el paquete promocional.
//    packPrice  -> precio del paquete completo.
//    grupo      -> (opcional) identificador para combinar/separar categorías.
//                  Las del mismo `grupo` se combinan; sin `grupo` se combinan
//                  por packSize+packPrice.
//
//  Ejemplo: 'monastery' y 'llamas' (ambas 2 x 50.000) se combinan entre sí.
// =============================================================================

export const PROMOS_ESPECIALES = {
    'caballista-clasica': { packSize: 2, packPrice: 50000 },
    'caballista-y-gallera-punta-de-cuero': { packSize: 2, packPrice: 50000 },
    'pasion-caballista': { packSize: 2, packPrice: 50000 },
    'belicas-mexicanas': { packSize: 2, packPrice: 50000 },
    'importada-monastery': { packSize: 2, packPrice: 50000 },
    'importada-nike-llamas': { packSize: 2, packPrice: 50000 },
    'importada-amiri-y-coach': { packSize: 2, packPrice: 50000 },
    'importada-clemont': { packSize: 2, packPrice: 50000 },
    'yeison-jimenez-apliques': { packSize: 2, packPrice: 50000 },
    'seleccion-colombia-placa': { packSize: 2, packPrice: 50000 },
    'seleccion-colombia-apliques': { packSize: 2, packPrice: 50000 },
    'equipos-de-futbol-colombia': { packSize: 2, packPrice: 50000 },
    'importada-gucci': { packSize: 2, packPrice: 50000 },
    'selecciones-mundialistas': { packSize: 2, packPrice: 50000 },
    'equipos-de-futbol-internacional': { packSize: 2, packPrice: 50000 },

    'beisbolera-completa': { packSize: 3, packPrice: 50000 },
    'beisbolera-malla': { packSize: 3, packPrice: 50000 },
    'soy-colombia': { packSize: 3, packPrice: 50000 },
    'goorin-promo': { packSize: 3, packPrice: 50000 },
};

// Límite de unidades en promoción (combinadas entre TODAS las categorías en
// promo). Hasta este número se aplica el precio de promoción; al superarlo,
// TODOS los productos en promoción pasan a precio mayorista (precioM).
export const LIMITE_UNIDADES_PROMO = 12;

// Normaliza un texto para comparar categorías sin importar mayúsculas/acentos.
const normalizar = (texto) =>
    (texto || '')
        .toString()
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '');

// Índice interno con las claves ya normalizadas (para búsquedas robustas).
const INDICE_PROMOS = Object.entries(PROMOS_ESPECIALES).reduce((acc, [nombre, promo]) => {
    acc[normalizar(nombre)] = promo;
    return acc;
}, {});

// Devuelve la promoción de una categoría, o null si la categoría no tiene promo.
export const getPromoEspecial = (nomCategoria) =>
    INDICE_PROMOS[normalizar(nomCategoria)] || null;

// Clave normalizada de una categoría (para agrupar productos por categoría).
export const claveCategoria = (nomCategoria) => normalizar(nomCategoria);

// Clave del GRUPO de una promoción: las categorías con la misma clave de grupo
// combinan sus unidades. Con `grupo` explícito se usa ese; si no, packSize+packPrice.
const claveGrupoPromo = (promo) =>
    promo?.grupo ? `g:${promo.grupo}` : `s:${promo.packSize}-${promo.packPrice}`;

// Clave de grupo a partir del nombre de la categoría (null si no tiene promo).
export const claveGrupo = (nomCategoria) => {
    const promo = getPromoEspecial(nomCategoria);
    return promo ? claveGrupoPromo(promo) : null;
};

// Categorías (nombres originales) que pertenecen a cada grupo, para los avisos.
const CATEGORIAS_POR_GRUPO = Object.entries(PROMOS_ESPECIALES).reduce((acc, [nombre, promo]) => {
    const key = claveGrupoPromo(promo);
    (acc[key] = acc[key] || []).push(nombre);
    return acc;
}, {});

// Nombres de las categorías de un grupo (para mostrar en los avisos de promo).
export const categoriasDelGrupo = (grupoKey) => CATEGORIAS_POR_GRUPO[grupoKey] || [];
